import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { CartAmountValidator } from '@next/core-lib/cart';
import { AbstractDialogResultData, AbstractFormDialog, AbstractFormDialogData, DIALOG_RETURN_TYPES, FormDialogComponent } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';

import { SiteState } from '@next/core-lib/site';
import { Store } from '@ngrx/store';

import { BehaviorSubject, combineLatest, map, Observable, Subject, take } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import {
  CropAid,
  FertilizerOrderPlanResponse,
  OrderLine,
  OrderLineStatus,
  DeliveryPeriodMomentType,
  TreatmentTypes,
  OrderPlanCropAidDisplayData,
  Package,
  PackageVersion,
  AllowedOrderRoles,
  FertilizerStockData,
  OrderableProduct,
} from '../../../shared/models';
import { roundToNearestMultiple, isNegative } from '../../../shared/utils';
import {
  getPackages,
  deleteOrderLine,
  saveFertilizerStock,
  loadFertilizerOrderPlan,
  saveOrderLine,
  getCropAidsByIds,
} from '../../store/actions';

import {
  selectFertilizerOrderLines,
  selectPackages,
  selectFertilizerOrderPlan,
  selectCropAidPackages,
  selectIsAutomaticAdviceCustomer,
} from '../../store/selectors';
import { selectCropYear } from '../../../store/selectors';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatSelect, MatOption } from '@angular/material/select';

export interface FertilizerProposalDialogData extends AbstractFormDialogData {
  orderPlan?: OrderPlanCropAidDisplayData[];
}

export interface FertilizerWithOrderLines extends OrderPlanCropAidDisplayData {
  orderLines: OrderLine[];
  isOrderable: boolean;
}

@Component({
  selector: 'app-fertilizer-proposal-dialog',
  templateUrl: './fertilizer-proposal-dialog.component.html',
  styleUrls: ['./fertilizer-proposal-dialog.component.scss'],
  imports: [
    FormDialogComponent,
    FormsModule,
    ReactiveFormsModule,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatIcon,
    NgClass,
    MatFormField,
    MatInput,
    MatIconButton,
    MatButton,
    MatLabel,
    MatSelect,
    MatOption,
    AsyncPipe,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class FertilizerProposalDialogComponent extends AbstractFormDialog<boolean> implements OnInit, OnDestroy {
  data = inject<FertilizerProposalDialogData>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef = inject<MatDialogRef<FertilizerProposalDialogComponent, AbstractDialogResultData<boolean>>>(MatDialogRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly localeService = inject(LocaleService);

  fertilizers$: Observable<(FertilizerWithOrderLines & { formIndex: number })[]>;
  formGroup: FormGroup;
  private readonly destroy$ = new Subject<void>();
  private readonly fertilizersData$ = new BehaviorSubject<(FertilizerWithOrderLines & { formIndex: number })[]>([]);

  currentlyAddingPackageFor: FertilizerWithOrderLines | null = null;
  availablePackagesForCurrentFertilizer: Package[] = [];
  selectedPackageId: number | null = null;

  private orderableProducts: OrderableProduct[] = [];
  private readonly expandedPanels = new Set<number>();
  private readonly formControlCache = new Map<string, FormControl>();

  readonly isNegative = isNegative;

  constructor() {
    super();
    this.formGroup = this.initializeForm();
    this.fertilizers$ = this.fertilizersData$.asObservable();
  }

  private initializeForm(): FormGroup {
    return this.formBuilder.group({
      fertilizers: this.formBuilder.array([]),
    });
  }

  private createNewOrderLine(
    packageId: number,
    cropAidId: number,
    selectedPackage: Package,
    cropAid: CropAid,
  ): OrderLine {
    return {
      id: 0,
      packageId,
      cropAidId,
      count: 0,
      year: this.currentYear,
      periodId: 1,
      status: OrderLineStatus.open,
      deliveryPeriodMoment: DeliveryPeriodMomentType.empty,
      treatmentType: TreatmentTypes.Fertilization,
      orderedDate: new Date().toISOString(),
      shipToId: 1,
      addedByOptimizer: false,
      cartId: -1,
      package: selectedPackage,
      cropAid,
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.fertilizersData$.complete();
  }

  get fertilizersFormArray(): FormArray {
    return this.formGroup.get('fertilizers') as FormArray;
  }

  get currentYear(): number {
    return new Date().getFullYear();
  }

  ngOnInit(): void {
    this.store.dispatch(getPackages());

    this.store.select(selectCropYear).pipe(take(1)).subscribe((cropYear) => {
      if (this.data && this.data.orderPlan) {
        const cropAidIds = [...new Set(this.data.orderPlan.map((item) => item.cropAid.id))];
        this.store.dispatch(getCropAidsByIds({ cropAidIds }));
      } else {
        this.store.dispatch(loadFertilizerOrderPlan({ year: cropYear }));
      }
    });

    combineLatest([
      this.store.select(selectFertilizerOrderPlan),
      this.store.select(selectFertilizerOrderLines),
      this.store.select(selectPackages),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([fertilizerOrderPlan, orderLines, packages]) => {
        if (!fertilizerOrderPlan || !orderLines) {
          return;
        }

        const currentData = this.fertilizersData$.value;

        if (currentData.length === 0) {
          this.orderableProducts = [
            ...fertilizerOrderPlan.orderableProducts,
            ...fertilizerOrderPlan.nonOrderableProducts,
          ];

          const formattedOrderPlan = this.formatFertilizerOrderPlan(fertilizerOrderPlan);

          const cropAidIds = [...new Set(formattedOrderPlan.map((item) => item.cropAid.id))];
          if (cropAidIds.length > 0) {
            this.store.dispatch(getCropAidsByIds({ cropAidIds }));
          }

          const fertilizers = this.processFertilizerData(
            formattedOrderPlan as (OrderPlanCropAidDisplayData & { isOrderable: boolean })[],
            orderLines,
            packages || [],
          );

          this.fertilizersData$.next(fertilizers);
        } else {
          this.updateFertilizerData(orderLines, packages || []);
        }
      });
  }

  private updatePackageReferences(packages: Package[]): void {
    const currentData = this.fertilizersData$.value;

    for (const fertilizer of currentData) {
      for (const orderLine of fertilizer.orderLines) {
        if (!orderLine.package) {
          const realPackage = packages.find((pkg) => pkg.id === orderLine.packageId);
          if (realPackage) {
            orderLine.package = realPackage;
          }
        }
      }
    }
  }

  private updateFertilizerData(orderLines: OrderLine[], packages: Package[]): void {
    const currentData = this.fertilizersData$.value;

    const orderLinesByCropAid = this.groupOrderLinesByCropAid(orderLines, packages);

    for (const fertilizer of currentData) {
      const updatedOrderLines = orderLinesByCropAid.get(fertilizer.cropAid.id) || [];

      const { newOrderLines, existingUpdated } = this.splitNewAndExistingOrderLines(
        fertilizer.orderLines,
        updatedOrderLines,
      );

      if (newOrderLines.length > 0) {
        this.addNewOrderLinesToFertilizer(fertilizer, newOrderLines);
      }

      if (existingUpdated.length > 0) {
        this.updateExistingOrderLinesInFertilizer(fertilizer, existingUpdated);
      }

      this.updatePackageReferences(packages);
    }

    this.fertilizersData$.next([...currentData]);
  }

  private groupOrderLinesByCropAid(orderLines: OrderLine[], packages: Package[]): Map<number, OrderLine[]> {
    const map = new Map<number, OrderLine[]>();

    for (const ol of orderLines) {
      if (ol.cropAidId == null) continue;
      const key = ol.cropAidId;
      const enriched = { ...ol, package: this.getPackageById(ol.packageId, packages) };
      const arr = map.get(key) ?? [];
      arr.push(enriched);
      map.set(key, arr);
    }

    return map;
  }

  private splitNewAndExistingOrderLines(
    currentOrderLines: OrderLine[],
    updatedOrderLines: OrderLine[],
  ): { newOrderLines: OrderLine[]; existingUpdated: OrderLine[] } {
    const currentIds = new Set(currentOrderLines.map((ol) => ol.id));
    const newOrderLines = updatedOrderLines.filter((ol) => !currentIds.has(ol.id));
    const existingUpdated = updatedOrderLines.filter((ol) => currentIds.has(ol.id));
    return { newOrderLines, existingUpdated };
  }

  private addNewOrderLinesToFertilizer(fertilizer: FertilizerWithOrderLines, newOrderLines: OrderLine[]): void {
    fertilizer.orderLines.push(...newOrderLines);

    const fertilizerFormIndex = this.findFertilizerFormIndex(fertilizer.cropAid.id);
    if (fertilizerFormIndex === -1) return;

    const orderLinesArray = this.getOrderLinesFormArray(fertilizerFormIndex);

    for (const newOrderLine of newOrderLines) {
      const newOrderLineFormGroup = this.formBuilder.group({
        id: [newOrderLine.id],
        count: [newOrderLine.count],
      });
      orderLinesArray.push(newOrderLineFormGroup);

      const countControl = newOrderLineFormGroup.get('count') as FormControl;
      this.addValidatorsToOrderLineCountControl(countControl, newOrderLine);
      this.setupOrderLineCountSubscription(countControl, newOrderLine);
    }

    this.recalculateOrderLinesTotalAmount(fertilizer);
  }

  private updateExistingOrderLinesInFertilizer(
    fertilizer: FertilizerWithOrderLines,
    updatedOrderLines: OrderLine[],
  ): void {
    const fertilizerFormIndex = this.findFertilizerFormIndex(fertilizer.cropAid.id);
    const orderLinesArray = fertilizerFormIndex === -1 ? null : this.getOrderLinesFormArray(fertilizerFormIndex);

    for (const updated of updatedOrderLines) {
      this.updateSingleOrderLine(fertilizer, updated, orderLinesArray);
    }

    this.recalculateOrderLinesTotalAmount(fertilizer);
  }

  private updateSingleOrderLine(
    fertilizer: FertilizerWithOrderLines,
    updated: OrderLine,
    orderLinesArray: FormArray | null,
  ): void {
    const existing = fertilizer.orderLines.find((ol) => ol.id === updated.id);
    if (existing && existing.count !== updated.count) {
      existing.count = updated.count;

      if (orderLinesArray) {
        this.updateOrderLineFormControl(orderLinesArray, updated);
      }
    }
  }

  private updateOrderLineFormControl(orderLinesArray: FormArray, updated: OrderLine): void {
    const formIndex = this.findFormOrderLineIndex(orderLinesArray, updated.id);
    if (formIndex === -1) return;

    const orderLineGroup = orderLinesArray.at(formIndex);
    const countControl = orderLineGroup.get('count');

    if (countControl && countControl.value !== updated.count) {
      countControl.setValue(updated.count, { emitEvent: false });
    }
  }

  private processFertilizerData(
    orderPlan: (OrderPlanCropAidDisplayData & { isOrderable: boolean })[],
    orderLines: OrderLine[],
    packages: Package[],
  ): (FertilizerWithOrderLines & { formIndex: number })[] {
    const groupedFertilizers = this.groupOrderPlanByCropAidId(orderPlan);

    this.fertilizersFormArray.clear();
    this.formControlCache.clear();

    const fertilizersWithOrderLines = groupedFertilizers.map((fertilizer, index) => {
      const orderLinesForFertilizer = orderLines
        .filter((orderLine) => orderLine.cropAidId === fertilizer.cropAid.id)
        .map((orderLine) => ({
          ...orderLine,
          package: this.getPackageById(orderLine.packageId, packages || []),
        }));

      const fertilizerFormGroup = this.formBuilder.group({
        cropAidId: [fertilizer.cropAid.id],
        stockAmount: [this.getCurrentStockValue(fertilizer.cropAid.id, fertilizer.customerProductInventoryAmount)],
        orderLines: this.formBuilder.array(
          orderLinesForFertilizer.map((orderLine) =>
            this.formBuilder.group({
              id: [orderLine.id],
              count: [this.getCurrentOrderLineValue(orderLine.id, orderLine.count)],
            }),
          ),
        ),
      });

      this.fertilizersFormArray.push(fertilizerFormGroup);

      this.setupStockAmountSubscription(fertilizerFormGroup, fertilizer.cropAid.id);

      const orderLinesArray = fertilizerFormGroup.get('orderLines') as FormArray;

      for (const [orderLineIndex, orderLine] of orderLinesForFertilizer.entries()) {
        const orderLineGroup = orderLinesArray.at(orderLineIndex);
        const countControl = orderLineGroup.get('count') as FormControl;
        this.addValidatorsToOrderLineCountControl(countControl, orderLine);
        this.setupOrderLineCountSubscription(countControl, orderLine);
      }

      return {
        ...fertilizer,
        orderLines: orderLinesForFertilizer,
        formIndex: index,
        isOrderable: fertilizer.isOrderable,
      } as FertilizerWithOrderLines & { formIndex: number };
    });

    return fertilizersWithOrderLines;
  }

  private formatFertilizerOrderPlan(fertilizerOrderPlan: FertilizerOrderPlanResponse): OrderPlanCropAidDisplayData[] {
    if (!fertilizerOrderPlan) {
      return [];
    }

    const orderableProducts = fertilizerOrderPlan.orderableProducts.map(
      (product) =>
        ({
          cropAid: {
            id: product.cropAidId,
            name: product.productName || 'Unknown Product',
            unitOfMeasurement: product.cropAidUnit || 'KG',
          } as CropAid,
          customerProductInventoryAmount: product.stockAmount || 0,
          orderLinesTotalAmount: product.amount || 0,
          requisiteAmount: product.requisiteAmount || 0,
          isOrderable: true,
        }) as OrderPlanCropAidDisplayData & { isOrderable: boolean },
    );

    const nonOrderableProducts = fertilizerOrderPlan.nonOrderableProducts.map(
      (product) =>
        ({
          cropAid: {
            id: product.cropAidId,
            name: product.productName || 'Unknown Product',
            unitOfMeasurement: product.cropAidUnit || 'KG',
          } as CropAid,
          customerProductInventoryAmount: product.stockAmount || 0,
          orderLinesTotalAmount: product.amount || 0,
          requisiteAmount: product.requisiteAmount || 0,
          isOrderable: false,
        }) as OrderPlanCropAidDisplayData & { isOrderable: boolean },
    );

    return [...orderableProducts, ...nonOrderableProducts];
  }

  private groupOrderPlanByCropAidId(
    orderPlan: (OrderPlanCropAidDisplayData & { isOrderable: boolean })[],
  ): (OrderPlanCropAidDisplayData & { isOrderable: boolean })[] {
    const groupedMap = new Map<number, OrderPlanCropAidDisplayData & { isOrderable: boolean }>();

    for (const item of orderPlan) {
      const cropAidId = item.cropAid.id;

      if (groupedMap.has(cropAidId)) {
        const existing = groupedMap.get(cropAidId)!;
        existing.orderLinesTotalAmount += item.orderLinesTotalAmount;
        existing.isOrderable = existing.isOrderable || item.isOrderable;
      } else {
        groupedMap.set(cropAidId, {
          cropAid: item.cropAid,
          customerProductInventoryAmount: item.customerProductInventoryAmount,
          orderLinesTotalAmount: item.orderLinesTotalAmount,
          requisiteAmount: item.requisiteAmount,
          isOrderable: item.isOrderable,
        });
      }
    }
    return Array.from(groupedMap.values());
  }

  private getPackageById(packageId: number, packages: Package[]): Package | undefined {
    return packages.find((pkg) => pkg.id === packageId);
  }

  getDifference(
    requisiteAmount: number,
    customerProductInventoryAmount: number,
    orderLinesTotalAmount: number,
  ): number {
    return customerProductInventoryAmount + orderLinesTotalAmount - requisiteAmount;
  }

  deleteOrderLine(orderLineId: number): void {
    const orderLine = this.fertilizersData$.value
      .flatMap((fertilizer) => fertilizer.orderLines)
      .find((candidate) => candidate.id === orderLineId);

    if (!orderLine) {
      return;
    }

    this.store.dispatch(deleteOrderLine({ orderLineId, year: orderLine.year }));

    const currentData = this.fertilizersData$.value;

    for (const fertilizer of currentData) {
      const orderLineIndex = fertilizer.orderLines.findIndex((ol) => ol.id === orderLineId);
      if (orderLineIndex !== -1) {
        fertilizer.orderLines.splice(orderLineIndex, 1);
        this.recalculateOrderLinesTotalAmount(fertilizer);

        const fertilizerFormIndex = this.findFertilizerFormIndex(fertilizer.cropAid.id);
        if (fertilizerFormIndex !== -1) {
          const orderLinesArray = this.getOrderLinesFormArray(fertilizerFormIndex);
          const formOrderLineIndex = this.findFormOrderLineIndex(orderLinesArray, orderLineId);
          if (formOrderLineIndex !== -1) {
            orderLinesArray.removeAt(formOrderLineIndex);
          }
        }
      }
    }

    this.fertilizersData$.next([...currentData]);
  }

  private recalculateOrderLinesTotalAmount(fertilizer: FertilizerWithOrderLines): void {
    fertilizer.orderLinesTotalAmount = fertilizer.orderLines.reduce((total, orderLine) => {
      const packageAmount = orderLine.package?.amount || 1;
      return total + orderLine.count * packageAmount;
    }, 0);
  }

  private findFormOrderLineIndex(orderLinesArray: FormArray, orderLineId: number): number {
    for (let i = 0; i < orderLinesArray.length; i++) {
      const orderLineGroup = orderLinesArray.at(i);
      if (orderLineGroup.get('id')?.value === orderLineId) {
        return i;
      }
    }
    return -1;
  }

  hasAvailablePackages(fertilizer: FertilizerWithOrderLines): Observable<boolean> {
    return combineLatest([
      this.store.select(selectCropAidPackages),
      this.store.select(selectPackages),
      this.store.select(selectIsAutomaticAdviceCustomer),
    ]).pipe(
      map(([cropAidPackages, packages, isAutomaticAdviceCustomer]) => {
        if (!cropAidPackages || cropAidPackages.length === 0) {
          return false;
        }

        const packagesForCropAid = cropAidPackages.filter((pkg) => pkg.id === fertilizer.cropAid.id);
        const usedPackageIds = new Set(fertilizer.orderLines.map((orderLine) => orderLine.packageId));
        const availablePackages = packagesForCropAid
          .filter((pkg) => !usedPackageIds.has(pkg.packageId))
          .filter((pkg) => {
            if (!isAutomaticAdviceCustomer) return true;
            return packages?.find((p) => p.id === pkg.packageId)?.allowedUsers === AllowedOrderRoles.All;
          });

        return availablePackages.length > 0;
      }),
    );
  }

  addPackage(fertilizer: FertilizerWithOrderLines): void {
    combineLatest([
      this.store.select(selectCropAidPackages),
      this.store.select(selectPackages),
      this.store.select(selectIsAutomaticAdviceCustomer),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([cropAidPackages, packages, isAutomaticAdviceCustomer]) => {
        if (cropAidPackages && cropAidPackages.length > 0 && packages && packages.length > 0) {
          const packagesForCropAid = cropAidPackages.filter((pkg) => pkg.id === fertilizer.cropAid.id);

          if (packagesForCropAid.length > 0) {
            const usedPackageIds = new Set(fertilizer.orderLines.map((orderLine) => orderLine.packageId));
            const availablePackagesForCropAid = packagesForCropAid.filter((pkg) => !usedPackageIds.has(pkg.packageId));

            const availablePackages: Package[] = availablePackagesForCropAid
              .map((cropAidPkg) => {
                const packageFromList = packages.find((pkg) => pkg.id === cropAidPkg.packageId);
                return this.createPackageFromCropAidPackage(cropAidPkg, packageFromList);
              })
              .filter((pkg) => !isAutomaticAdviceCustomer || pkg.allowedUsers === AllowedOrderRoles.All);

            this.currentlyAddingPackageFor = fertilizer;
            this.availablePackagesForCurrentFertilizer = availablePackages;
            this.selectedPackageId = null;
          }
        }
      });
  }

  confirmPackageSelection(): void {
    if (this.currentlyAddingPackageFor && this.selectedPackageId) {
      const selectedPackage = this.availablePackagesForCurrentFertilizer.find(
        (pkg) => pkg.id === this.selectedPackageId,
      );

      if (selectedPackage) {
        const orderLine = this.createNewOrderLine(
          this.selectedPackageId,
          this.currentlyAddingPackageFor.cropAid.id,
          selectedPackage,
          this.currentlyAddingPackageFor.cropAid,
        );

        this.store.dispatch(saveOrderLine({ orderLine }));

        this.cancelPackageSelection();
      }
    }
  }

  cancelPackageSelection(): void {
    this.currentlyAddingPackageFor = null;
    this.availablePackagesForCurrentFertilizer = [];
    this.selectedPackageId = null;
  }

  isAddingPackageFor(fertilizer: FertilizerWithOrderLines): boolean {
    return this.currentlyAddingPackageFor === fertilizer;
  }

  getSelectedPackage(): Package | null {
    return this.selectedPackageId
      ? this.availablePackagesForCurrentFertilizer.find((pkg) => pkg.id === this.selectedPackageId) || null
      : null;
  }

  private saveStockAmount(cropAidId: number, amount: number): void {
    if (!Number.isFinite(amount) || amount < 0 || (amount !== 0 && roundToNearestMultiple(amount, 0.01) !== amount)) {
      return;
    }

    const fertilizerStockData: FertilizerStockData = {
      cropAidId: cropAidId,
      stock: amount,
    };
    this.store.dispatch(saveFertilizerStock({ cropYear: this.currentYear, fertilizerStock: [fertilizerStockData] }));

    const currentData = this.fertilizersData$.value;

    for (const fertilizer of currentData) {
      if (fertilizer.cropAid.id === cropAidId) {
        fertilizer.customerProductInventoryAmount = amount;
      }
    }

    this.fertilizersData$.next([...currentData]);
  }

  private updateOrderLineCount(orderLine: OrderLine, newCount: number): void {
    const updatedOrderLine: OrderLine = {
      ...orderLine,
      count: newCount,
    };
    this.store.dispatch(saveOrderLine({ orderLine: updatedOrderLine }));

    const currentData = this.fertilizersData$.value;

    for (const fertilizer of currentData) {
      const localOrderLine = fertilizer.orderLines.find((ol) => ol.id === orderLine.id);
      if (localOrderLine) {
        localOrderLine.count = newCount;
        this.recalculateOrderLinesTotalAmount(fertilizer);
      }
    }

    this.fertilizersData$.next([...currentData]);
  }

  private createPackageFromCropAidPackage(
    cropAidPkg: { packageId: number; id: number; name: string; unitOfMeasurement: string; amount: number },
    packageFromList?: Package,
  ): Package {
    return {
      id: cropAidPkg.packageId,
      productId: cropAidPkg.id,
      name: packageFromList?.name || cropAidPkg.name,
      unitOfMeasurement: packageFromList?.unitOfMeasurement || cropAidPkg.unitOfMeasurement,
      amount: packageFromList?.amount || cropAidPkg.amount,
      isActive: packageFromList?.isActive ?? true,
      packageVersion: packageFromList?.packageVersion ?? PackageVersion.V2,
      allowedUsers: packageFromList?.allowedUsers ?? AllowedOrderRoles.None,
    };
  }

  private setupOrderLineCountSubscription(orderLineControl: FormControl, orderLine: OrderLine): void {
    orderLineControl.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(1500)).subscribe((newCount) => {
      if (newCount !== null && newCount !== undefined) {
        const increaseOrderUnitQuantity = this.getOrderLineIncreaseOrderUnitQuantity(orderLine);
        const countToUse = orderLineControl.valid
          ? newCount
          : roundToNearestMultiple(newCount, increaseOrderUnitQuantity);

        if (!orderLineControl.valid) {
          orderLineControl.setValue(countToUse, { emitEvent: false });
        }

        this.updateOrderLineCount(orderLine, countToUse);
      }
    });
  }

  private setupStockAmountSubscription(formGroup: FormGroup, cropAidId: number): void {
    formGroup
      .get('stockAmount')
      ?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(500), distinctUntilChanged())
      .subscribe((newValue) => {
        if (newValue !== null && newValue !== undefined) {
          this.saveStockAmount(cropAidId, newValue);
        }
      });
  }

  private findFertilizerFormIndex(cropAidId: number): number {
    for (let i = 0; i < this.fertilizersFormArray.length; i++) {
      const formGroup = this.fertilizersFormArray.at(i);
      if (formGroup.get('cropAidId')?.value === cropAidId) {
        return i;
      }
    }
    return -1;
  }
  getOrderLineIncreaseOrderUnitQuantity(orderLine: OrderLine): number {
    const orderableProduct = this.orderableProducts.find(
      (product) => product.cropAidId === orderLine.cropAidId && product.packageId === orderLine.packageId,
    );
    return orderableProduct?.increaseOrderUnitQuantity ?? 1;
  }

  private getOrderLinesFormArray(formIndex: number): FormArray {
    if (formIndex >= this.fertilizersFormArray.length) {
      return this.formBuilder.array([]);
    }
    const fertilizerFormGroup = this.fertilizersFormArray.at(formIndex);
    if (!fertilizerFormGroup) {
      return this.formBuilder.array([]);
    }
    return fertilizerFormGroup.get('orderLines') as FormArray;
  }

  getStockControl(formIndex: number): FormControl {
    if (formIndex >= this.fertilizersFormArray.length) {
      const actualValue = this.getStockAmountFromData(formIndex);
      return new FormControl(actualValue);
    }
    const fertilizerFormGroup = this.fertilizersFormArray.at(formIndex);
    if (!fertilizerFormGroup) {
      const actualValue = this.getStockAmountFromData(formIndex);
      return new FormControl(actualValue);
    }
    return fertilizerFormGroup.get('stockAmount') as FormControl;
  }

  getOrderLineCountControlById(orderLineId: number, fertilizerIndex: number, orderLineIndex: number): FormControl {
    const cacheKey = `orderline-id-${orderLineId}`;

    const orderLinesArray = this.getOrderLinesFormArray(fertilizerIndex);
    if (orderLinesArray && orderLineIndex < orderLinesArray.length && orderLineIndex >= 0) {
      const orderLineGroup = orderLinesArray.at(orderLineIndex);
      const countControl = orderLineGroup?.get('count');
      if (countControl) {
        this.formControlCache.set(cacheKey, countControl as FormControl);
        return countControl as FormControl;
      }
    }

    return this.getCachedOrCreateControl(cacheKey, orderLineId);
  }

  private getCachedOrCreateControl(cacheKey: string, orderLineId: number): FormControl {
    const cachedControl = this.formControlCache.get(cacheKey);
    if (cachedControl) {
      return cachedControl;
    }

    const actualValue = this.getOrderLineValueFromData(orderLineId);
    const newControl = new FormControl(actualValue);
    this.formControlCache.set(cacheKey, newControl);
    return newControl;
  }

  private getStockAmountFromData(fertilizerIndex: number): number {
    if (fertilizerIndex < this.fertilizersFormArray.length) {
      const fertilizerFormGroup = this.fertilizersFormArray.at(fertilizerIndex);
      const stockControl = fertilizerFormGroup?.get('stockAmount');
      if (stockControl && stockControl.value !== null && stockControl.value !== undefined) {
        return stockControl.value;
      }
    }

    const fertilizersData = this.fertilizersData$.value;
    if (!fertilizersData || fertilizerIndex >= fertilizersData.length) return 0;

    const fertilizer = fertilizersData[fertilizerIndex];
    return fertilizer?.customerProductInventoryAmount ?? 0;
  }

  private getCurrentStockValue(cropAidId: number, fallbackValue: number): number {
    for (let i = 0; i < this.fertilizersFormArray.length; i++) {
      const formGroup = this.fertilizersFormArray.at(i);
      const currentCropAidId = formGroup.get('cropAidId')?.value;
      if (currentCropAidId === cropAidId) {
        const stockControl = formGroup.get('stockAmount');
        if (stockControl && stockControl.value !== null && stockControl.value !== undefined) {
          return stockControl.value;
        }
      }
    }
    return fallbackValue;
  }

  private getCurrentOrderLineValue(orderLineId: number, fallbackValue: number): number {
    const cachedValue = this.getCachedOrderLineValue(orderLineId);
    if (cachedValue !== null) {
      return cachedValue;
    }

    const formValue = this.getOrderLineValueFromForm(orderLineId);
    return formValue ?? fallbackValue;
  }

  private getCachedOrderLineValue(orderLineId: number): number | null {
    const cacheKey = `orderline-id-${orderLineId}`;
    const cachedControl = this.formControlCache.get(cacheKey);

    if (!cachedControl) {
      return null;
    }

    return this.isValidValue(cachedControl.value) ? cachedControl.value : null;
  }

  private getOrderLineValueFromForm(orderLineId: number): number | null {
    for (let i = 0; i < this.fertilizersFormArray.length; i++) {
      const formGroup = this.fertilizersFormArray.at(i);
      const orderLinesArray = formGroup.get('orderLines') as FormArray;

      if (!orderLinesArray) {
        continue;
      }

      const value = this.findOrderLineValueInArray(orderLinesArray, orderLineId);
      if (value !== null) {
        return value;
      }
    }
    return null;
  }

  private findOrderLineValueInArray(orderLinesArray: FormArray, orderLineId: number): number | null {
    for (let j = 0; j < orderLinesArray.length; j++) {
      const orderLineGroup = orderLinesArray.at(j);
      const currentOrderLineId = orderLineGroup.get('id')?.value;

      if (currentOrderLineId !== orderLineId) {
        continue;
      }

      const countControl = orderLineGroup.get('count');
      if (!countControl) {
        return null;
      }

      return this.isValidValue(countControl.value) ? countControl.value : null;
    }
    return null;
  }

  private isValidValue(value: unknown): boolean {
    return value !== null && value !== undefined;
  }

  private getOrderLineValueFromData(orderLineId: number): number {
    const fertilizersData = this.fertilizersData$.value;
    if (!fertilizersData) return 0;

    for (const fertilizer of fertilizersData) {
      if (fertilizer.orderLines) {
        for (const orderLine of fertilizer.orderLines) {
          if (orderLine.id === orderLineId) {
            return orderLine.count ?? 0;
          }
        }
      }
    }
    return 0;
  }

  private addValidatorsToOrderLineCountControl(countControl: FormControl, orderLine: OrderLine): void {
    const orderableProduct = this.orderableProducts.find(
      (product) => product.cropAidId === orderLine.cropAidId && product.packageId === orderLine.packageId,
    );

    if (orderableProduct?.increaseOrderUnitQuantity) {
      const validator = new CartAmountValidator(
        0,
        orderableProduct.maximumOrderUnitQuantity ?? 999999,
        orderableProduct.increaseOrderUnitQuantity,
        this.localeService,
      );
      countControl.setValidators([(control: AbstractControl) => validator.validate(control)]);
      countControl.updateValueAndValidity();
    }
  }

  onPanelOpened(cropAidId: number): void {
    this.expandedPanels.add(cropAidId);
  }

  onPanelClosed(cropAidId: number): void {
    this.expandedPanels.delete(cropAidId);
  }

  isPanelExpanded(cropAidId: number): boolean {
    return this.expandedPanels.has(cropAidId);
  }

  cancel(): void {
    this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.cancel });
  }

  process(): void {
    this.ai.trackEvent('AddFertilizerToCart', {});
    this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.confirm, result: true });
  }

  alt(): void {
    this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.alt });
  }
}
